import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DealStatus, Organization, OrgBranding, OrgProfile, Role } from "@biedradar/shared/pro";
import { api, type AgentInput, type BidInput, type ClientPatch, type FinancialsInput, type MortgageInput, type PropertyPatch, type ViewingInput } from "./api";

export const qk = {
  me: ["me"] as const,
  org: ["org"] as const,
  members: ["members"] as const,
  clients: ["clients"] as const,
  deals: ["deals"] as const,
  deal: (id: string) => ["deal", id] as const,
  agenda: ["agenda"] as const,
  orgAgents: ["org-agents"] as const,
  orgProperties: ["org-properties"] as const,
  memberInvites: ["member-invites"] as const,
  templates: ["deal-templates"] as const,
};

/** The org's custom deal templates (new-deal picker). */
export function useTemplates() {
  return useQuery({ queryKey: qk.templates, queryFn: api.listTemplates });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.templates }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.templates }),
  });
}

/** Every property across the org's deals (the /Properties page). */
export function useOrgProperties() {
  return useQuery({ queryKey: qk.orgProperties, queryFn: api.listOrgProperties });
}

/** Every viewing across the org's deals (cross-deal Agenda page). */
export function useAgenda() {
  return useQuery({ queryKey: qk.agenda, queryFn: api.listAgenda });
}

/** Every selling agent across the org's deals (the /Sellers page). */
export function useOrgAgents() {
  return useQuery({ queryKey: qk.orgAgents, queryFn: api.listOrgAgents });
}

/**
 * Current advisor identity + memberships + active org (GET /api/pro/me).
 * Backs the session guard, Topbar, and onboarding. `retry: false` so an auth
 * error surfaces immediately instead of being retried.
 */
export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: api.getMe, retry: false });
}

export function useOrg() {
  return useQuery({ queryKey: qk.org, queryFn: api.getOrg });
}

export function useMembers() {
  return useQuery({ queryKey: qk.members, queryFn: api.getMembers });
}

export function useDeals() {
  return useQuery({ queryKey: qk.deals, queryFn: api.listDeals });
}

export function useDeal(id: string) {
  return useQuery({
    queryKey: qk.deal(id),
    queryFn: () => api.getDeal(id),
    enabled: id.length > 0,
  });
}

/** Save the client's financial profile, then refresh the deal (gap recomputes
 *  server-side) and the portfolio list (its gap pill). */
export function useUpdateFinancials(dealId: string, clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FinancialsInput) => api.updateFinancials(clientId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.deals });
    },
  });
}

/** Save the deal's mortgage profile, then refresh the deal + portfolio. */
export function useUpdateMortgage(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MortgageInput) => api.updateMortgage(dealId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.deals });
    },
  });
}

/** Edit the client's contact details; refresh the deal + portfolio + clients. */
export function useUpdateClient(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ClientPatch }) => api.updateClient(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.deals });
      void qc.invalidateQueries({ queryKey: qk.clients });
    },
  });
}

/** Edit the deal's property (price / value / coords); refresh deal + portfolio. */
export function useUpdateProperty(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PropertyPatch) => api.updateProperty(dealId, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.deals });
    },
  });
}

/** Upsert the deal's latest bid; refresh deal + portfolio (drives the gap). */
export function useUpsertBid(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BidInput) => api.upsertBid(dealId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.deals });
    },
  });
}

/** Viewings (the property step's "viewed many homes" list). Each mutation also
 *  refreshes the cross-deal Agenda. */
export function useAddViewing(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ViewingInput) => api.addViewing(dealId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.agenda });
    },
  });
}
export function useUpdateViewing(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ViewingInput> }) => api.updateViewing(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.agenda });
    },
  });
}
export function useDeleteViewing(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteViewing(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.agenda });
    },
  });
}

/** Selling agents (makelaars) on the deal. */
export function useAddAgent(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AgentInput) => api.addAgent(dealId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.orgAgents });
    },
  });
}
export function useUpdateAgent(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<AgentInput> }) => api.updateAgent(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.orgAgents });
    },
  });
}
export function useDeleteAgent(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAgent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.deal(dealId) });
      void qc.invalidateQueries({ queryKey: qk.orgAgents });
    },
  });
}

/** Patch a step's `records` JSON (merged server-side). Backs the property step's
 *  per-step address + linked-agent editors; refreshes the deal so the canvas
 *  node labels recompute. */
export function useUpdateStepRecords(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, records }: { stepId: string; records: Record<string, unknown> }) =>
      api.updateStep(stepId, { records }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.deal(dealId) }),
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { name?: string; branding?: OrgBranding | null; profile?: OrgProfile | null }) => api.updateOrg(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.org });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

/** Dismiss the flow/board view-toggle discovery hint (org-wide, one-time). */
export function useDismissViewHint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.dismissViewHint(),
    // Optimistically flip the cached org so the hint disappears immediately.
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: qk.org });
      const prev = qc.getQueryData<Organization>(qk.org);
      if (prev) qc.setQueryData<Organization>(qk.org, { ...prev, viewHintDismissed: true });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.org, ctx.prev);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.org }),
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => api.updateMemberRole(id, role),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.members }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.removeMember(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.members }),
  });
}

/** Patch a deal (status / title / assignee) from the portfolio row menu. */
export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { status?: DealStatus; title?: string; description?: string | null; assigneeUserId?: string | null } }) =>
      api.updateDeal(id, patch),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.deals });
      void qc.invalidateQueries({ queryKey: qk.deal(id) });
    },
  });
}

export function useDeleteDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteDeal(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.deals }),
  });
}

export function useMemberInvites() {
  return useQuery({ queryKey: qk.memberInvites, queryFn: api.listMemberInvites });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: Role }) => api.inviteMember(email, role),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.memberInvites }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeMemberInvite(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.memberInvites }),
  });
}
